interface LeadAssignmentRecord {
  userId: string;
  timestamp: number;
}

const STORAGE_KEY = 'vendedor_lead_assignments';
const MAX_LEADS_PER_HOUR = 5;

class LeadAssignmentTracker {
  private static instance: LeadAssignmentTracker;

  public static getInstance(): LeadAssignmentTracker {
    if (!LeadAssignmentTracker.instance) {
      LeadAssignmentTracker.instance = new LeadAssignmentTracker();
    }
    return LeadAssignmentTracker.instance;
  }

  private getStoredAssignments(): LeadAssignmentRecord[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error reading lead assignments from localStorage:', error);
      return [];
    }
  }

  private saveAssignments(assignments: LeadAssignmentRecord[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
    } catch (error) {
      console.error('Error saving lead assignments to localStorage:', error);
    }
  }

  private cleanExpiredAssignments(assignments: LeadAssignmentRecord[]): LeadAssignmentRecord[] {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    return assignments.filter(assignment => assignment.timestamp > oneHourAgo);
  }

  public getAssignmentsInLastHour(userId: string): number {
    const assignments = this.getStoredAssignments();
    const validAssignments = this.cleanExpiredAssignments(assignments);
    
    // Save cleaned assignments back to storage
    if (validAssignments.length !== assignments.length) {
      this.saveAssignments(validAssignments);
    }

    return validAssignments.filter(assignment => assignment.userId === userId).length;
  }

  public canAssignLead(userId: string): boolean {
    const currentCount = this.getAssignmentsInLastHour(userId);
    return currentCount < MAX_LEADS_PER_HOUR;
  }

  public recordLeadAssignment(userId: string): boolean {
    if (!this.canAssignLead(userId)) {
      return false;
    }

    const assignments = this.getStoredAssignments();
    const validAssignments = this.cleanExpiredAssignments(assignments);
    
    const newAssignment: LeadAssignmentRecord = {
      userId,
      timestamp: Date.now(),
    };

    validAssignments.push(newAssignment);
    this.saveAssignments(validAssignments);
    
    return true;
  }

  public getTimeUntilNextSlot(userId: string): number {
    const assignments = this.getStoredAssignments();
    const validAssignments = this.cleanExpiredAssignments(assignments)
      .filter(assignment => assignment.userId === userId)
      .sort((a, b) => a.timestamp - b.timestamp);

    if (validAssignments.length === 0) {
      return 0;
    }

    // If we have 5 assignments, the next slot opens when the oldest expires
    if (validAssignments.length >= MAX_LEADS_PER_HOUR) {
      const oldestAssignment = validAssignments[0];
      const expirationTime = oldestAssignment.timestamp + (60 * 60 * 1000);
      return Math.max(0, expirationTime - Date.now());
    }

    return 0;
  }

  public getNextAvailableTime(userId: string): Date | null {
    const timeUntilNext = this.getTimeUntilNextSlot(userId);
    if (timeUntilNext === 0) return null;
    return new Date(Date.now() + timeUntilNext);
  }
}

export default LeadAssignmentTracker;